import { c as _c } from "react/compiler-runtime";
import { animated, useSpring } from '@react-spring/web';
import React, { ComponentProps, FunctionComponent, memo, useCallback } from 'react';
import useMeasure from 'react-use-measure';
import { IconDeprecated } from '../../misc/IconDeprecated/IconDeprecated';
import { prepareProps } from '../../utils/propUtils';
import { classes } from '../../utils/utils';
import styles from './Accordion.module.css';
export type AccordionProps = Omit<ComponentProps<'div'>, 'onToggle'> & {
  /**
   * Controls whether the accordion is opened or closed.
   */
  open: boolean;
  /**
   * Called when the user opens or closes the accordion.
   * The provided parameter `open` is what the prop `open` should be set to.
   */
  onToggle: (open: boolean) => void;
  /**
   * If set to `true`, prevents the user from opening or closing the accordion.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * The content displayed above the children.
   */
  summary: React.ReactNode;
};
export const Accordion: FunctionComponent<AccordionProps> = memo(props => {
  const $ = _c(40);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let children;
  let onToggle;
  let open;
  let remainingProps;
  let summary;
  let t1;
  if ($[2] !== t0) {
    ({
      open,
      onToggle,
      disabled: t1,
      summary,
      children,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = children;
    $[4] = onToggle;
    $[5] = open;
    $[6] = remainingProps;
    $[7] = summary;
    $[8] = t1;
  } else {
    children = $[3];
    onToggle = $[4];
    open = $[5];
    remainingProps = $[6];
    summary = $[7];
    t1 = $[8];
  }
  const disabled = t1 === undefined ? false : t1;
  const [ref, t2] = useMeasure();
  const {
    height: contentHeight
  } = t2;
  let t3;
  let t4;
  if ($[9] !== contentHeight || $[10] !== open) {
    t3 = () => ({
      height: open ? contentHeight : 0
    });
    t4 = [open, contentHeight];
    $[9] = contentHeight;
    $[10] = open;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  const [animatedHeight] = useSpring(t3, t4);
  let t5;
  if ($[13] !== disabled || $[14] !== onToggle || $[15] !== open) {
    t5 = () => {
      if (!disabled) {
        onToggle(!open);
      }
    };
    $[13] = disabled;
    $[14] = onToggle;
    $[15] = open;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const onDetailsClick = t5;
  const t6 = disabled && styles.disabled;
  let t7;
  if ($[17] !== t6) {
    t7 = classes(styles.summary, t6);
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== summary) {
    t8 = <div data-testid="Accordion.Summary">{summary}</div>;
    $[19] = summary;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className={styles.summaryLine} />;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = open ? "chevron-up" : "chevron-down";
  let t11;
  if ($[22] !== t10) {
    t11 = <IconDeprecated className={styles.summaryIcon} name={t10} />;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== onDetailsClick || $[25] !== t11 || $[26] !== t7 || $[27] !== t8) {
    t12 = <div className={t7} onClick={onDetailsClick}>{t8}{t9}{t11}</div>;
    $[24] = onDetailsClick;
    $[25] = t11;
    $[26] = t7;
    $[27] = t8;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const t13 = !open;
  let t14;
  if ($[29] !== children || $[30] !== ref || $[31] !== t13) {
    t14 = <div ref={ref} data-testid="Accordion.Content" aria-hidden={t13}>{children}</div>;
    $[29] = children;
    $[30] = ref;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== animatedHeight || $[34] !== t14) {
    t15 = <animated.div style={animatedHeight} className={styles.content}>{t14}</animated.div>;
    $[33] = animatedHeight;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== remainingProps || $[37] !== t12 || $[38] !== t15) {
    t16 = <div {...remainingProps}>{t12}{t15}</div>;
    $[36] = remainingProps;
    $[37] = t12;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
});